import { useEffect, useState } from 'react';
import { Button, Col, Form, Input, Row, Select, Switch } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProps } from 'antd/es/form/Form';

// services
import NotificationService from 'core/services/notification';
import { CreateCityPayload, onGetCity, onUpdateCity } from 'modules/cities/services/cities';
import { onGetAllRegions } from 'modules/regions/services/regions';

// components
import BackButton from 'components/backButton/backButton';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'name[en]': string;
	'name[ar]': string;
	region_id: number;
	active: 0 | 1;
};

function UpdateCityPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [regions, setRegions] = useState<{ label: string; value: number }[]>([]);
	const [updateForm] = Form.useForm();

	useEffect(() => {
		getCity();
	}, []);

	const getCity = async () => {
		try {
			const cityResponse = await onGetCity(id as string);
			const city = cityResponse.data.data;

			const regionsResponse = await onGetAllRegions();
			setRegions(
				regionsResponse.data.data.map((el: any) => {
					return { label: el.name, value: el.id };
				}),
			);

			updateForm.setFieldsValue({ 'name[ar]': city.name.ar });
			updateForm.setFieldsValue({ 'name[en]': city.name.en });
			updateForm.setFieldsValue({ region_id: city.region.id });
			updateForm.setFieldsValue({ active: city.active });

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			navigate('/cities');
		}
	};

	const onStatusChange = async (checked: boolean) => {
		updateForm.setFieldValue('active', checked ? 1 : 0);
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const updateCityPayload: Partial<CreateCityPayload> = {
				name: {
					ar: values['name[ar]'],
					en: values['name[en]'],
				},
				active: values.active,
				region_id: values.region_id,
			};

			await onUpdateCity(id as string, updateCityPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('CITIES.UPDATED_SUCCESS'));
			navigate('/cities');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('CITIES.UPDATE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('CITIES.UPDATE_TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className="sectionContainer">
					<h5 style={{ marginBottom: 20 }}>{t('CITIES.DETAILS')}</h5>

					<Form name="updateForm" form={updateForm} onFinish={onSubmit}>
						<h6>{t('CITIES.INFORMATION')}</h6>
						<Row gutter={[20, 20]}>
							<Col span={24} md={12}>
								<Form.Item<FieldType> name="name[en]" rules={[{ required: true, message: t('CITIES.VALIDATION.NAME_EN') }]}>
									<Input size="large" placeholder={t('CITIES.PLACEHOLDER.NAME_EN')} />
								</Form.Item>
							</Col>
							<Col span={24} md={12}>
								<Form.Item<FieldType> name="name[ar]" rules={[{ required: true, message: t('CITIES.VALIDATION.NAME_AR') }]}>
									<Input size="large" placeholder={t('CITIES.PLACEHOLDER.NAME_AR')} />
								</Form.Item>
							</Col>
						</Row>

						<h6>{t('CITIES.REGION')}</h6>
						<Row gutter={[20, 20]}>
							<Col span={24} md={12}>
								<Form.Item<FieldType> name="region_id" rules={[{ required: true, message: t('CITIES.VALIDATION.REGION') }]}>
									<Select
										style={{ width: '100%' }}
										size="large"
										options={regions}
										loading={isLoading}
										showSearch
										filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row>
							<Col span={24} md={12}>
								<div className="d-flex gap-2 mb-3">
									<Form.Item<FieldType> name="active" hidden>
										<Input />
									</Form.Item>
									<Switch defaultChecked={updateForm.getFieldValue('active')} onChange={onStatusChange} />
									<p className="mb-0 fw-medium">{t('COMMON.STATUS')}</p>
								</div>
							</Col>
						</Row>
					</Form>

					<Form.Item>
						<Button
							size="middle"
							type="primary"
							htmlType="submit"
							form="updateForm"
							loading={isSubmitting}
							style={{ marginTop: 20, display: 'flex' }}>
							{t('COMMON.UPDATE')}
						</Button>
					</Form.Item>
				</div>
			)}
		</>
	);
}

export default UpdateCityPage;
